import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import {
    Typography,
    Button,
} from '@material-ui/core';
import NumberInput from '../../../../../common/components/UI/Input/Number';
import RadioButton from '../../../../../common/components/UI/RadioGroup/RadioButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { RollingPeriodUnit } from '../../../../Enums';
const DefaultFieldValidationErrors = Object.freeze({
    maxHoursGreaterThanPeriod: false,
});

const MAX_HOURS_MAXIMUM = 300;
const ROLLING_PERIOD_MAXIMUM_IN_DAYS = 31;
const ROLLING_PERIOD_MINIMUM_IN_DAYS = 1;
const ROLLING_PERIOD_MAXIMUM_IN_HOURS = ROLLING_PERIOD_MAXIMUM_IN_DAYS * 24;

/**
 * @param {{
 *  isDisabled: boolean,
 *  onAdd: Function,
 * }}
 */
export default function MaxHoursPerRollingPeriod(props) {
    const { isDisabled, onAdd } = props;
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'settings.ruleSet.scheduleRulesStep.rollingPeriodRules' });

    const [maxHoursInRollingPeriod, setMaxHoursInRollingPeriod] = useState(1);
    const [rollingPeriodNumber, setRollingPeriodNumber] = useState(1);
    const [rollingPeriodUnit, setRollingPeriodUnit] = useState(RollingPeriodUnit.Hours);
    const [fieldValidationErrors, setFieldValidationErrors] = React.useState(cloneDeep(DefaultFieldValidationErrors));

    const resetValues = () => {
        setMaxHoursInRollingPeriod(1);
        setRollingPeriodNumber(1);
        setRollingPeriodUnit(RollingPeriodUnit.Hours);
        setFieldValidationErrors(cloneDeep(DefaultFieldValidationErrors));
    }
    
    const validateCurrentAwardingRule = () => {
        let isValid = true;
        const errors = cloneDeep(DefaultFieldValidationErrors);
        const rollingPeriodInHours = rollingPeriodUnit === RollingPeriodUnit.Hours ? rollingPeriodNumber : rollingPeriodNumber * 24;

        if (maxHoursInRollingPeriod > rollingPeriodInHours) {
            errors.maxHoursGreaterThanPeriod = true;
            isValid = false;
        }

        for (const property in errors){
            if (errors[property] !== fieldValidationErrors[property]){
                setFieldValidationErrors(errors);
            }
        }

        return isValid;
    }

    const handleAddRule = () => {
        if (validateCurrentAwardingRule()) {
            onAdd(maxHoursInRollingPeriod, rollingPeriodNumber, rollingPeriodUnit);
            resetValues();
        }
    };

    /**
     * @param {RollingPeriodUnit} unit 
     */
    const onChangeRollingPeriodUnit = (unit) => {
        setRollingPeriodUnit(unit);
        let rollingPeriodValue = 1;
        if (unit === RollingPeriodUnit.Hours){
            const valueInHours = rollingPeriodNumber * 24;
            rollingPeriodValue = Math.min(valueInHours, ROLLING_PERIOD_MAXIMUM_IN_HOURS);
        }
        else {
            const valueInDays = rollingPeriodNumber / 24;
            rollingPeriodValue = Math.max(valueInDays, ROLLING_PERIOD_MINIMUM_IN_DAYS)
        }
        setRollingPeriodNumber(rollingPeriodValue);
    }

    return (
        <>
            <div className="mhrp-number-input">
                <NumberInput
                    handleChangeInput={(value, target) => setMaxHoursInRollingPeriod(value)}
                    min={1}
                    max={MAX_HOURS_MAXIMUM}
                    step={1}
                    precision={0}
                    value={maxHoursInRollingPeriod}
                    isDisabled={isDisabled}
                    strict={true}
                    target='maxHoursInRollingPeriod'
                    error={fieldValidationErrors.maxHoursGreaterThanPeriod}
                />
                <Typography variant="body1">
                    {t('maxHours')}
                </Typography>
            </div>
            {
                fieldValidationErrors.maxHoursGreaterThanPeriod ? (
                    <span style={{ color: 'red', display: 'block' }}>{t('errors.maxHoursGreaterThanPeriod')}</span>) : null
            }
            <div className="mhrp-rolling-period">
                <Typography variant="body1">
                    {t('rollingPeriod')}
                </Typography>
                <NumberInput
                    handleChangeInput={(value, target) => setRollingPeriodNumber(value)}
                    min={1}
                    max={rollingPeriodUnit === RollingPeriodUnit.Hours ? ROLLING_PERIOD_MAXIMUM_IN_HOURS : ROLLING_PERIOD_MAXIMUM_IN_DAYS}
                    step={1}
                    precision={0}
                    value={rollingPeriodNumber}
                    isDisabled={isDisabled}
                    strict={true}
                    target='rollingPeriodNumber'
                    error={false}
                />
                <RadioButton
                    radioGroupClass={'srs-timeUnitContainer'}
                    valueRadioGroup={rollingPeriodUnit !== null ? rollingPeriodUnit.toString() : null}
                    onChange={(event) => onChangeRollingPeriodUnit(parseInt(event.target.value))}
                    labelA={'Hours'}
                    valueA={RollingPeriodUnit.Hours.toString()}
                    labelB={'Days'}
                    valueB={RollingPeriodUnit.Days.toString()}
                    isRadioADisabled={isDisabled}
                    isRadioBDisabled={isDisabled}
                />
            </div>
            <div className="mhrp-buttons-container">
                <Button
                    color="primary"
                    variant="contained"
                    onClick={resetValues}
                    disabled={isDisabled}
                    className="srs--button"
                >
                    {t('clear')}
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleAddRule}
                    disabled={isDisabled}
                    className="srs--button"
                >
                    <>
                        <FontAwesomeIcon
                            className="mhrp-selection-add-update-btn"
                            icon={faPlus}
                        />
                        {t('addRule')}
                    </>
                </Button>
                </div>
        </>
    );
}